import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import { useComplianceData } from '../../hooks/useComplianceData';
import { useComplianceDocumentStats } from '../../hooks/useComplianceDocumentStats';
import { useCompliance } from '../../contexts/ComplianceContext';
import { ReportPDF } from '../items/Dashboard/ReportPDF';
import ComplianceDocumentReports from '../items/Compliance/ComplianceDocumentReports';
import s from '../../styles/Compliance.module.css';

const ComplianceAgencyDetail: React.FC = () => {
  const { agencyId } = useParams<{ agencyId: string }>();
  const { complianceUser } = useCompliance();
  const {
    agencies, fetchAgencies,
    listings, listingsLoading, fetchAgencyListings,
    documentMap, documentMapLoading, fetchAgencyDocumentMap,
    downloadAgencyDocumentMap,
  } = useComplianceData();
  const {
    agencyUsers, agencyStats, categoriesWithStats,
    loading: statsLoading, fetchAgencyDocumentStats,
  } = useComplianceDocumentStats();
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const agency = agencies.find(a => a.id === agencyId);
  const agencyName = agency?.company_name ?? 'Agency';

  useEffect(() => {
    if (agencies.length === 0) fetchAgencies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!agencyId) return;
    fetchAgencyListings(agencyId);
    fetchAgencyDocumentMap(agencyId);
    fetchAgencyDocumentStats(agencyId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId]);

  const handleDownloadReport = async () => {
    if (!agencyStats) return;
    setDownloadingPdf(true);
    try {
      const agentRows = agencyUsers.map(u => ({
        id: u.user.id,
        name: u.user.displayName,
        email: u.user.email,
        completionRate: u.stats.overall.completionRate,
      }));

      const blob = await pdf(
        <ReportPDF
          agencyName={agencyName}
          generatedAt={new Date().toLocaleString('en-ZA')}
          userName={complianceUser?.email ?? 'Compliance Officer'}
          role="Compliance Officer"
          isManager={true}
          overallStats={agencyStats}
          categoriesWithStats={categoriesWithStats}
          agentRows={agentRows}
          listings={listings}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${agencyName}-compliance-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadDocumentMap = async () => {
    if (!agencyId) return;
    setDownloadingZip(true);
    try {
      await downloadAgencyDocumentMap(agencyId, agencyName);
    } finally {
      setDownloadingZip(false);
    }
  };

  return (
    <div className={s.page}>
      <h2 className={s.pageTitle}>🏢 {agencyName}</h2>

      <section className={s.panel}>
        <h3 className={s.panelTitle}>Document Compliance Stats</h3>
        {statsLoading ? (
          <p className={s.loading}>Loading…</p>
        ) : (
          <ComplianceDocumentReports
            overallStats={agencyStats ?? { totalItems: 0, completedItems: 0, missingItems: 0, expiredItems: 0, completionRate: 0 }}
            categoriesWithStats={categoriesWithStats}
            agencyUsers={agencyUsers}
          />
        )}
      </section>

      <section className={s.panel}>
        <h3 className={s.panelTitle}>Listings</h3>
        {listingsLoading ? (
          <p className={s.loading}>Loading…</p>
        ) : (
          <p>{listings.length} listing{listings.length !== 1 ? 's' : ''} found.</p>
        )}
      </section>

      <section className={s.panel}>
        <h3 className={s.panelTitle}>Reports &amp; Exports</h3>
        <div className={s.formActions}>
          <button
            className={s.btnSave}
            onClick={handleDownloadReport}
            disabled={downloadingPdf || statsLoading || listingsLoading || !agencyStats}
          >
            {downloadingPdf ? 'Generating…' : '⬇ Download Compliance & Listings Report (PDF)'}
          </button>
        </div>
      </section>

      <section className={s.panel}>
        <h3 className={s.panelTitle}>Document Map</h3>
        {documentMapLoading ? (
          <p className={s.loading}>Loading…</p>
        ) : documentMap ? (
          <p>
            {documentMap.totalDocuments} document{documentMap.totalDocuments !== 1 ? 's' : ''} across{' '}
            {documentMap.userCount} user{documentMap.userCount !== 1 ? 's' : ''} (plus company-level documents),
            organized into {documentMap.folders.length} folder{documentMap.folders.length !== 1 ? 's' : ''}.
          </p>
        ) : null}
        <div className={s.formActions}>
          <button className={s.btnSave} onClick={handleDownloadDocumentMap} disabled={downloadingZip}>
            {downloadingZip ? 'Preparing ZIP…' : '⬇ Download Document Map (.zip)'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default ComplianceAgencyDetail;
